const AUTH_URL = "https://psicodosis-auth.psicodosis2024.workers.dev";
export const GOOGLE_AUTH_URL = `${AUTH_URL}/auth/google`;

export interface User {
  id: string;
  email: string;
  nombre: string;
  created_at: string;
  plan: "free" | "premium";
  avatar_url: string | null;
  /** Foto subida manualmente (tabla `perfiles`). Distinta de avatar_url (foto de Google). */
  foto_url?: string | null;
}

export interface Profile {
  id: string;
  interes: string;
  objetivo: string;
  nivel_afectacion: number;
  created_at: string;
}

interface ApiOk<T>    { ok: true;  data: T }
interface ApiError    { ok: false; error: { code: string; message: string; email?: string } }
type ApiResult<T> = ApiOk<T> | ApiError;

async function call<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResult<T>> {
  try {
    const res = await fetch(`${AUTH_URL}${path}`, {
      headers: { "Content-Type": "application/json", ...options.headers },
      ...options,
    });
    return (await res.json()) as ApiResult<T>;
  } catch (err) {
    return { ok: false, error: { code: "NETWORK_ERROR", message: "No se pudo conectar con el servidor" } };
  }
}

// ─── Public Generacion API (no auth required) ─────────────────────────────
const GENERACION_URL = "https://psicodosis-generacion.psicodosis2024.workers.dev";

export interface GuiaPublica {
  id: string;
  categoria: string;
  titulo: string;
  url_r2: string;
  nombre_archivo: string;
  created_at: string;
}

export interface MaterialPublico {
  id: string;
  categoria: string;
  tipo: string;
  titulo: string;
  descripcion: string | null;
  url_r2: string;
  nombre_archivo: string;
  created_at: string;
}

export const getGuiaCategoria = (categoria: string) =>
  fetch(`${GENERACION_URL}/guias/categoria/${categoria.toUpperCase()}`)
    .then((r) => r.json()) as Promise<{ ok: boolean; data: GuiaPublica; error?: { message: string } }>;

export const getTodasGuias = () =>
  fetch(`${GENERACION_URL}/guias`)
    .then((r) => r.json()) as Promise<{ ok: boolean; data: { guias: GuiaPublica[]; total: number }; error?: { message: string } }>;

export const getMaterialCategoria = (categoria: string) =>
  fetch(`${GENERACION_URL}/material?categoria=${categoria.toUpperCase()}`)
    .then((r) => r.json()) as Promise<{ ok: boolean; data: { material: MaterialPublico[]; total: number }; error?: { message: string } }>;

// ─── Perfil ───────────────────────────────────────────────────────────────
export interface PerfilUsuario {
  id: string;
  user_id: string;
  nombre: string | null;
  email: string | null;
  foto_url: string | null;
  bio: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Foro ─────────────────────────────────────────────────────────────────
export interface ForoTema {
  id: string;
  titulo: string;
  descripcion: string | null;
  categoria: string;
  creado_por: string;
  activo: number;
  created_at: string;
  post_count: number;
  ultima_actividad: string;
  creador_nombre: string | null;
  creador_avatar_url: string | null;
  creador_foto_url: string | null;
}

export interface ForoPost {
  id: string;
  tema_id: string;
  parent_id: string | null;
  user_id: string;
  nombre_usuario: string;
  contenido: string;
  likes: number;
  created_at: string;
  liked_by_user: boolean;
  replies: ForoPost[];
  avatar_url: string | null;
  foto_url: string | null;
}

export const api = {
  register: (data: { email: string; password: string; nombre: string }) =>
    call<{ message: string }>("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  login: (email: string, password: string) =>
    call<{ token: string; user: User }>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  verificarEmail: (token: string) =>
    call<{ token: string; user: User }>(`/auth/verificar?token=${encodeURIComponent(token)}`),

  reenviarVerificacion: (email: string) =>
    call<{ message: string }>("/auth/reenviar-verificacion", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  me: (token: string) =>
    call<{ user: User; profile: Profile | null }>("/auth/me", {
      headers: { Authorization: `Bearer ${token}` },
    }),

  saveProfile: (
    data: { interes: string; objetivo: string; nivel_afectacion: number },
    token: string
  ) =>
    call<{ profile: Profile }>("/auth/profile", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    }),

  // ── Perfil de usuario ──────────────────────────────────────────────────
  getPerfil: (token: string) =>
    call<{ perfil: PerfilUsuario | null }>("/perfil", {
      headers: { Authorization: `Bearer ${token}` },
    }),

  updatePerfil: (data: { nombre?: string; bio?: string }, token: string) =>
    call<{ perfil: PerfilUsuario }>("/perfil", {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    }),

  subirFotoPerfil: (data: { foto_base64: string; mime_type: string }, token: string) =>
    call<{ foto_url: string }>("/perfil/foto", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    }),

  cambiarPassword: (
    data: { password_actual: string; password_nueva: string },
    token: string
  ) =>
    call<{ changed: boolean }>("/auth/cambiar-password", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    }),

  // ── Foro ────────────────────────────────────────────────────────────────
  getForoTemas: (categoria?: string) => {
    const qs = categoria ? `?categoria=${encodeURIComponent(categoria)}` : "";
    return call<{ temas: ForoTema[]; total: number }>(`/foro/temas${qs}`);
  },

  getForoTema: (id: string) =>
    call<{ tema: ForoTema }>(`/foro/temas/${id}`),

  getForoPosts: (tema_id: string, token?: string) =>
    call<{ posts: ForoPost[]; total: number }>(`/foro/posts/${tema_id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }),

  crearPost: (
    data: { tema_id: string; contenido: string; parent_id?: string },
    token: string
  ) =>
    call<{ post: ForoPost }>("/foro/posts", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    }),

  toggleLike: (post_id: string, token: string) =>
    call<{ liked: boolean; likes: number }>(`/foro/likes/${post_id}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }),

  // ── Membresías / Stripe ────────────────────────────────────────────────
  crearSesionPago: (token: string, plan: "plus" | "pro" | "max") =>
    call<{ url: string }>("/stripe/crear-sesion", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ plan }),
    }),

  getSubscripcion: (token: string) =>
    call<{ plan_tier: "libre" | "plus" | "pro" | "max"; tiene_suscripcion_activa: boolean }>(
      "/stripe/subscripcion",
      { headers: { Authorization: `Bearer ${token}` } }
    ),

  // ── Usuario ─────────────────────────────────────────────────────────────
  getUsuarioStats: (token: string) =>
    call<{ ejercicios_completados: number; aportes_comunidad: number }>("/usuario/stats", {
      headers: { Authorization: `Bearer ${token}` },
    }),
};
